import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  FeedbackSupplierProfile,
  MassRenewalDownloadFile,
} from '@erp/rpc';

import { orpc, rpcClient } from '@/api/rpc/client';
import {
  startMassRenewalRun,
  type MassRenewalDrop,
} from '@/api/pricing/startMassRenewalRun';
import type { UploadStartPhase } from '@/api/pricing/stageWorkbooks';
import { peekWorkbook } from '../hubbell-feedback/peekWorkbook';
import { openDownload } from '../hubbell-feedback/openDownload';
import { LATEST_RUN_POLLING } from '../hubbell-feedback/runDisplay';
import { useLatestRun } from '../hubbell-feedback/useLatestRun';
import {
  classifyRenewalWorkbook,
  RENEWAL_PEEK_ROWS,
  renewalDropState,
  type DroppedRenewalFile,
} from './classifyRenewalFiles';
import {
  renewalStartBlocker,
  type RenewalDownloadTarget,
} from './massRenewalRunDisplay';

async function classify(
  file: File,
  supplier: FeedbackSupplierProfile,
): Promise<DroppedRenewalFile['kind']> {
  try {
    return classifyRenewalWorkbook(
      await peekWorkbook(file, RENEWAL_PEEK_ROWS),
      supplier,
      file.name,
    );
  } catch {
    return { kind: 'unreadable' };
  }
}

/**
 * A supplier's Mass Renewal card state: the files dropped and what each is,
 * the organization's latest run for that supplier (polled every 2 s while it
 * goes, so leaving the page and coming back finds it), and the
 * upload-and-start in flight.
 */
export function useMassRenewal(supplier: FeedbackSupplierProfile) {
  const queryClient = useQueryClient();
  const latestRunOptions = orpc.pricing.massRenewal.getLatestRun.queryOptions({
    input: { supplier: supplier.supplier },
    ...LATEST_RUN_POLLING,
  });
  const { allowed, runQuery, run, setLatestRun } =
    useLatestRun(latestRunOptions);

  const [entries, setEntries] = useState<DroppedRenewalFile[]>([]);
  const nextId = useRef(0);
  const [phase, setPhase] = useState<UploadStartPhase | null>(null);
  const [downloading, setDownloading] = useState<RenewalDownloadTarget | null>(
    null,
  );

  /** Adds the files as "reading" and classifies each from its first rows. */
  function addFiles(files: File[]): void {
    const added = files.map((file): DroppedRenewalFile => {
      nextId.current += 1;
      return { id: String(nextId.current), file, kind: { kind: 'reading' } };
    });
    setEntries((current) => [...current, ...added]);
    for (const entry of added) {
      void classify(entry.file, supplier).then((kind) =>
        setEntries((current) =>
          current.map((item) =>
            item.id === entry.id ? { ...item, kind } : item,
          ),
        ),
      );
    }
  }

  function removeFile(id: string): void {
    setEntries((current) => current.filter((entry) => entry.id !== id));
  }

  const drop = renewalDropState(entries);

  const start = useMutation({
    mutationFn: (input: MassRenewalDrop) =>
      startMassRenewalRun(input, setPhase),
    onSuccess: (started) => {
      // The started (or already active) run is the latest; polling takes over.
      setLatestRun(started);
      // Refetch it too, so the card shows the server's view of the run at once.
      void queryClient.invalidateQueries({
        queryKey: latestRunOptions.queryKey,
      });
      setEntries([]);
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => setPhase(null),
  });

  async function download(
    letter: string,
    file: MassRenewalDownloadFile,
  ): Promise<void> {
    if (!run) return;
    setDownloading({ letter, file });
    try {
      const link = await rpcClient.pricing.massRenewal.getDownloadUrl({
        backgroundJobId: run.backgroundJobId,
        letter,
        file,
      });
      openDownload(link.url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
    setDownloading(null);
  }

  return {
    run,
    runLoading: runQuery.isLoading,
    runError: runQuery.isError,
    drop,
    addFiles,
    removeFile,
    phase,
    blocker: renewalStartBlocker({
      drop,
      run,
      allowed,
      starting: start.isPending,
    }),
    /** Uploads the drop and starts the run; nothing happens before the drop is complete. */
    start: () => {
      if (!drop.resultsWorkbook || drop.issues.length > 0) return;
      start.mutate({
        supplier: supplier.supplier,
        resultsWorkbook: drop.resultsWorkbook,
        feedbackFiles: drop.feedbackFiles,
        exceptionLists: drop.exceptionLists,
      });
    },
    download,
    downloading,
  };
}
